//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useSelector } from 'react-redux'
import {
  useLocationQueryParams,
  useSetSearchParamsWithQueryString,
} from 'utils/hooks'


import { useSortableColumnsOptions } from 'components/Shared/SortDropdown/hooks'

import {
  useFetchTicketProviderTicketsQuery,
  useReloadTicketProviderTicketsMutation,
  useDeleteTicketProviderTicketsMutation,
} from 'api/ticketProviderTickets'

import {
  useFetchTicketProviderTicketFiltersQuery,
} from 'api/ticketProviderTicketFilters'

import { AbilityContext } from 'ability-context'
import {
  RouteTicketProviders,
  RouteTicketProviderTickets,
} from 'routes'

import colors from 'components/utils/colors'

import { confirm } from 'components/Shared/ConfirmDialog/Index'
import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import ExportCsv from 'components/Shared/ExportCsv/Index'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import ScrollableTabsPanel from 'components/App/ScrollableTabsPanel/Index'
import Header from 'components/Shared/Header/Index'
import ActionButtons from 'components/Shared/Skeleton/ActionButtons/Index'
import FilterPanel from 'components/Shared/Skeleton/FilterPanel/Index'
import FilterPanelTitle from 'components/Shared/Skeleton/FilterPanel/FilterPanelTitle'
import UnwrappableSection from 'components/Shared/Skeleton/FilterPanel/UnwrappableSection'
import FilterPanelSearch from 'components/Shared/Skeleton/FilterPanel/FilterPanelSearch'
import SortDropdown from 'components/Shared/SortDropdown/Index'
import IconLink from 'components/Shared/IconLink/Index'
import ClearFiltersIcon from 'components/Shared/ClearFiltersIcon/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import TicketsTable from './shared/TicketsTable/Index'

import FiltersSidebar from './FiltersSidebar/Index'
import TicketSidebar from './shared/TicketSidebar'

import MixedFiltersField from 'components/Shared/MixedFiltersField/Index'
import RowsCountAndPagination from 'components/Shared/RowsCountAndPagination/Index'

import CardsListPanel from 'components/Shared/CardsListPanel/Index'

import * as sessionUserSelector from 'selectors/sessionUser'

const TicketProviderTicketsPage = () => {
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [bulkDelete, setBulkDelete] = useState(false)
  const [checkedTicketIds, setCheckedTicketIds] = useState([])
  const [checkedTicketExternalIds, setCheckedTicketExternalIds] = useState([])
  const [isTicketFilterRemoving, setIsTicketFilterRemoving] = useState(false)
  const [needRemoveCsvFile, setNeedRemoveCsvFile] = useState(false)
  const [isFiltersSidebarVisible, setIsFiltersSidebarVisible] = useState(false)

  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const ability = useContext(AbilityContext)
  const setQueryParam = useSetSearchParamsWithQueryString()

  const [locationQueryParams] = useLocationQueryParams()
  const activePage = parseInt(locationQueryParams.page) || 1
  const searchValue = locationQueryParams.search || ''
  const statusFilters = locationQueryParams.status_filters || []

  const ticketProviderId = params.ticketProviderId || null

  const ticketProviders = useSelector(sessionUserSelector.ticketProviders)
  const selectedTicketProvider = ticketProviders.find(tp => tp.id === ticketProviderId)
  const providerTicketBoards = selectedTicketProvider?.providerTicketBoards || []

  const selectedProviderTicketBoardId =
    locationQueryParams.provider_ticket_board_id || providerTicketBoards[0]?.id

  const providerTicketBoard = providerTicketBoards.find(ptb => ptb.id === selectedProviderTicketBoardId)

  const defaultSortField = providerTicketBoard?.defaultSortField
  const defaultSortOrder = providerTicketBoard?.defaultSortOrder

  let sortField = locationQueryParams.sort_field || defaultSortField
  let sortDir = locationQueryParams.sort_dir || defaultSortOrder
  const activeTicketFilterIds = locationQueryParams.ticket_filters || []

  const { data, isFetching } = useFetchTicketProviderTicketsQuery({
    ticketProviderId: ticketProviderId,
    page: activePage,
    searchValue: searchValue,
    statusFilters: statusFilters,
    userFilters: activeTicketFilterIds,
    sortField: sortField,
    sortDir: sortDir,
    providerTicketBoardId: selectedProviderTicketBoardId,
  })

  const { data: ticketFiltersData, isFetching: isTicketFiltersFetching } = useFetchTicketProviderTicketFiltersQuery({
    ticketProviderId: ticketProviderId,
    providerTicketBoardId: selectedProviderTicketBoardId,
  })

  const csvExportType = 'new_provider_tickets'

  const [reloadTicketProviderTickets, { isLoading: isReloadingTickets }] = useReloadTicketProviderTicketsMutation()
  const [deleteTicketProviderTickets, { isLoading: isDeletingTickets }] = useDeleteTicketProviderTicketsMutation()

  // clear selected ticket if it is absent in fetched tickets
  useEffect(() => {
    if (isFetching) {
      return
    }

    const hasSelectedTicket = selectedTicket && data.tickets.some(t => t.id === selectedTicket.id)
    if (!hasSelectedTicket) {
      setSelectedTicket(null)
    }
  }, [isFetching]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBulkDelete(false)
  }, [ticketProviderId])

  const tickets = data?.tickets || []
  const ticketProviderName = data?.metaData?.relatedSystems?.ticketProvider?.name
  const partnerStatuses = data?.partnerStatuses || []
  const partnerStatusesMap = data?.partnerStatusesMap || {}
  const ticketsCount = data?.ticketsCount || 0
  const totalPages = data?.totalPages || 1
  const sortableColumns = data?.sortableColumns || []
  const ticketsIds = tickets.map(t => t.id)
  const ticketsExternalIds = tickets.map(t => t.externalId)
  const relatedSystems = data?.metaData?.relatedSystems || null

  // Disable reload tickets for ama_kinesis_case provider
  // due to different data in kinesis stream and AMA GET response payload
  const reloadTicketsAvailable = data?.reloadTickets

  if (!sortableColumns.includes(sortField) && sortField !== 'id') {
    sortField = 'id'
  }

  const sortDirs = ['asc', 'desc']

  if (!sortDirs.includes(sortDir)) {
    sortDir = 'desc'
  }

  const additionalSortableColumnsParams = [{ value: 'id', label: t('ticketProviderTicketsPage.sortRelayTicketIdLabel').toUpperCase() }]
  const sortableColumnsOptions = useSortableColumnsOptions(sortableColumns, additionalSortableColumnsParams)

  const exportingRecordId = data?.exportingRecordId

  const ticketFilters = ticketFiltersData?.filters || []

  const getBreadcrumbs = () => {
    const ticketProvidersUrl = RouteTicketProviders()

    const breadcrumbsList = [{
      text: t('common.breadcrumbTicketProviders'),
      url: ticketProvidersUrl,
    }, {
      text: ticketProviderName,
    }]
    return breadcrumbsList
  }

  const onTicketSelected = (ticket) => {
    setSelectedTicket(ticket)
  }

  const onCsvRemoving = useCallback(() => {
    setNeedRemoveCsvFile(false)
  }, [])

  const onTicketFilterRemoving = useCallback((value) => {
    setIsTicketFilterRemoving(value)
  }, [])

  const toggleFiltersSidebar = () => {
    setIsFiltersSidebarVisible(!isFiltersSidebarVisible)
  }

  const showFiltersSidebar = () => {
    setIsFiltersSidebarVisible(true)
  }

  const hideFiltersSidebar = () => {
    setIsFiltersSidebarVisible(false)
  }

  const onSidebarClose = () => {
    setSelectedTicket(null)
  }

  const getDefaultUrlParams = () => {
    const params = {
      page: 1,
      search: searchValue,
      status_filters: statusFilters,
      ticket_filters: activeTicketFilterIds,
      sort_field: defaultSortField,
      sort_dir: defaultSortOrder,
      provider_ticket_board_id: selectedProviderTicketBoardId,
    }

    return params
  }

  const filterParams = {
    'search': searchValue,
    'statusFilters': statusFilters,
    'userFilters': activeTicketFilterIds,
    'ticketProviderId': data?.metaData?.relatedSystems?.ticketProvider?.id,
    'providerTicketBoardId': selectedProviderTicketBoardId,
  }

  const updateURL = (params) => {
    const newParams = {
      ...getDefaultUrlParams(),
      ...params,
    }

    setQueryParam(newParams)
  }

  const onSearch = (newSearchValue) => {
    setNeedRemoveCsvFile(true)
    updateURL({
      search: newSearchValue,
    })
  }

  // newFilters = { ticket_filters: [...], status_filters: [...] }
  const onFilterChange = (newFilters) => {
    setNeedRemoveCsvFile(true)
    updateURL(newFilters)
  }

  const onFilterClear = () => {
    onFilterChange({ ticket_filters: [], status_filters: [] })
  }

  const reloadTickets = () => {
    reloadTicketProviderTickets({ ticketProviderId: ticketProviderId }).then(fulfilled => {
      if (!fulfilled.data?.error) {
        navigate({
          pathname: RouteTicketProviderTickets({ ticketProviderId: ticketProviderId }),
        })
      }
    })
  }

  const onSortFieldChange = ({ sortField, sortDir }) => {
    updateURL({
      sort_field: sortField,
      sort_dir: sortDir,
    })
  }

  const onToggleBulkDelete = () => {
    setCheckedTicketIds([])
    setCheckedTicketExternalIds([])
    setBulkDelete(!bulkDelete)
  }

  const onDeleteCheckedTickets = () => {
    const title = t('ticketsPage.removeTicketsTitle')
    const message = t('ticketsPage.removeTicketsMessage',
      {
        count: checkedTicketExternalIds.length,
        numbers: checkedTicketExternalIds.join(', '),
      }
    )

    confirm({
      title: title,
      message: message,
    }).then(
      () => {
        deleteTicketProviderTickets({
          ticketProviderId: ticketProviderId,
          ticketIds: checkedTicketIds,
        }).then(fulfilled => {
          setBulkDelete(false)
          setCheckedTicketIds([])
          setCheckedTicketExternalIds([])
          if (!fulfilled.data?.error) {
            navigate({
              pathname: RouteTicketProviderTickets({ ticketProviderId: ticketProviderId }),
            })
          }
        })
      },
      () => {}
    )
  }

  // if all tickets on the current page are checked, uncheck them
  // otherwise check all tickets on the current page
  // keep checked tickets from other pages
  const onCheckAllRowsToggle = () => {
    const checkedTicketsIdsOnPage = checkedTicketIds.filter(id => ticketsIds.includes(id))
    const checkedTicketsExternalIdsOnPage = checkedTicketExternalIds.filter(externalId => ticketsExternalIds.includes(externalId))

    if (ticketsIds.length === checkedTicketsIdsOnPage.length) {
      const newTicketsIds = checkedTicketIds.filter(id => !checkedTicketsIdsOnPage.includes(id))
      setCheckedTicketIds(newTicketsIds)

      const newTicketsExternalIds = checkedTicketExternalIds.filter(id => !checkedTicketsExternalIdsOnPage.includes(id))
      setCheckedTicketExternalIds(newTicketsExternalIds)
    } else {
      const checkedIdsAtOtherPages = checkedTicketIds.filter(id => !ticketsIds.includes(id))
      setCheckedTicketIds([...checkedIdsAtOtherPages, ...ticketsIds])

      const checkedExternalIdsAtOtherPages = checkedTicketExternalIds.filter(externalId => !ticketsExternalIds.includes(externalId))
      setCheckedTicketExternalIds([...checkedExternalIdsAtOtherPages, ...ticketsExternalIds])
    }
  }

  const onRowCheck = (ticketId, ticketExternalId) => {
    if (checkedTicketIds.includes(ticketId)) {
      const newTicketsIds = checkedTicketIds.filter(id => id !== ticketId)
      setCheckedTicketIds(newTicketsIds)

      const newTicketsExternalIds = checkedTicketExternalIds.filter(id => id !== ticketExternalId)
      setCheckedTicketExternalIds(newTicketsExternalIds)
    } else {
      setCheckedTicketIds([...checkedTicketIds, ticketId])

      setCheckedTicketExternalIds([...checkedTicketExternalIds, ticketExternalId])
    }
  }

  const onTicketBoardSelect = (ticketBoard) => {
    const params = {
      provider_ticket_board_id: ticketBoard.id,
      sort_field: ticketBoard.defaultSortField,
      sort_dir: ticketBoard.defaultSortOrder,
      ticket_filters: [],
      status_filters: [],
    }

    updateURL(params)
  }

  const buildSelectedilterList = () => {
    const result = []

    activeTicketFilterIds.forEach((filterId) => {
      const filter = ticketFilters.find(filter => filter.id === filterId)

      if (filter) {
        const otherFilterIds = activeTicketFilterIds.filter(id => id !== filter.id)
        let color = colors.pausedGray

        if (filter.global) {
          color = filter.published ? colors.globalGray : colors.infoGray
        }

        result.push({
          value: filter.name,
          color: color,
          onClear: () => onFilterChange({ ticket_filters: otherFilterIds }),
        })
      }
    })

    return result
  }

  const buildSelectedMultipleFilterList = () => {
    if (!statusFilters?.length) {
      return []
    }

    const items = []

    statusFilters.forEach((statusName) => {
      const status = partnerStatuses.find(s => s.name === statusName)

      if (status) {
        items.push({ value: status.name, bgColor: status.color })
      }
    })

    return [{
      title: t('ticketProviderTicketsPage.filtersStatusSectionTitle'),
      items: items,
      clearTooltip: t('ticketProviderTicketsPage.filterPanelClearStatusesTooltip'),
      onClear: () => onFilterChange({ status_filters: [] }),
    }]
  }

  if (!ticketProviderId) {
    return <ErrorPage404 />
  }

  const clearFiltersUrl =
    `${RouteTicketProviderTickets({ ticketProviderId: ticketProviderId })}?provider_ticket_board_id=${selectedProviderTicketBoardId}`

  const canDeleteTickets = ability.can('delete', 'Ticket')
  const canReloadTickets = ability.can('reload', 'Tickets') && reloadTicketsAvailable
  const isAnyActiveFilters = statusFilters.length > 0 || activeTicketFilterIds.length > 0
  const isAnyActiveFiltersOrSearch = searchValue || isAnyActiveFilters

  const isLoading = isFetching || isReloadingTickets || isDeletingTickets ||
    isTicketFiltersFetching || isTicketFilterRemoving

  const fixedHeader = (
    <ScrollableTabsPanel
      relatedSystems={relatedSystems}
    />
  )

  return (
    <MainWrap
      pageTitle={ticketProviderName}
      fixedHeader={fixedHeader}
    >
      <FiltersSidebar
        isVisible={isFiltersSidebarVisible}
        onClose={hideFiltersSidebar}
        ticketProviderId={ticketProviderId}
        activeTicketFilterIds={activeTicketFilterIds}
        ticketFilters={ticketFilters}
        onFilterChange={onFilterChange}
        onTicketFilterRemoving={onTicketFilterRemoving}
        isTicketFiltersFetching={isTicketFiltersFetching}
        partnerStatuses={partnerStatuses}
        activeStatusFilters={statusFilters}
        selectedProviderTicketBoardId={selectedProviderTicketBoardId}
        providerTicketBoards={providerTicketBoards}
      />
      <TicketSidebar
        ticket={selectedTicket}
        onClose={onSidebarClose}
        searchValue={searchValue}
      />
      <Content loading={isLoading}>
        <Header
          title={ticketProviderName}
          breadcrumbsList={getBreadcrumbs()}
        />
        <ActionButtons>
          {canReloadTickets && (
            <IconLink
              onClick={reloadTickets}
              iconName='icon-refetch'
              text={t('ticketProviderTicketsPage.reloadTicketsLink')}
            />
          )}
          {canDeleteTickets && (
            <>
              <IconLink
                active={bulkDelete}
                onClick={onToggleBulkDelete}
                iconName='icon-remove'
                text={t('ticketProviderTicketsPage.bulkRemoveLink')}
              />
              {bulkDelete && checkedTicketIds.length > 0 && (
                <IconLink
                  onClick={onDeleteCheckedTickets}
                  iconName='icon-delete-list'
                  text={t('ticketProviderTicketsPage.removeTicketsLink')}
                />
              )}
            </>
          )}
          <ExportCsv
            type={csvExportType}
            recordCount={ticketsCount}
            exportingRecordId={exportingRecordId}
            isFetching={isLoading}
            filterParams={filterParams}
            needRemoveCsvFile={needRemoveCsvFile}
            onCsvRemoving={onCsvRemoving}
          />
        </ActionButtons>

        {(providerTicketBoards.length > 1) && (
          <CardsListPanel
            onSelect={onTicketBoardSelect}
            cards={providerTicketBoards}
            selectedCardId={selectedProviderTicketBoardId}
            noPadding
            noUpperStyle
          />
        )}

        <FilterPanel dataTest='tickets-page-filter-panel'>
          <FilterPanelTitle
            title={t('ticketProviderTicketsPage.filterPanelTitle')}
            tooltipText={t('ticketProviderTicketsPage.filterPanelTitleTooltip')}
            onClick={toggleFiltersSidebar}
          />

          <MixedFiltersField
            filterList={buildSelectedilterList()}
            multipleFilterList={buildSelectedMultipleFilterList()}
            maxDisplayItems={3}
            onClear={onFilterClear}
            onClearTooltip={t('ticketProviderTicketsPage.filterPanelClearFiltersTooltip')}
            tooltipText={t('ticketProviderTicketsPage.filterPanelFiltersTooltip')}
            tooltipId='tooltip-filter-panel'
            onContractorClick={showFiltersSidebar}
          />

          <UnwrappableSection>
            <FilterPanelSearch
              key={searchValue}
              initialValue={searchValue}
              onChange={onSearch}
              placeholder={t('ticketProviderTicketsPage.searchPlaceholderNew')}
              buttonTooltip={t('ticketProviderTicketsPage.filterPanelSearchTooltip')}
              tooltipId='tooltip-filter-panel'
            />
            {isAnyActiveFiltersOrSearch && (
              <ClearFiltersIcon
                clearUrl={clearFiltersUrl}
                tooltipText={t('ticketProviderTicketsPage.clearFiltersLink')}
              />
            )}
            <div />
          </UnwrappableSection>

          <SortDropdown
            title={t('common.sortedBy')}
            onChange={onSortFieldChange}
            value={{ sortField: sortField, sortDir: sortDir }}
            options={sortableColumnsOptions}
            defaultSortField={defaultSortField}
            defaultSortDir={defaultSortOrder}
            clearActionTooltip={t('ticketProviderTicketsPage.filterPanelClearSortingTooltip')}
          />
          <Tooltip id='tooltip-filter-panel' />
        </FilterPanel>

        <TicketsTable
          tickets={tickets}
          selectedTicketId={selectedTicket?.id}
          onRowClick={onTicketSelected}
          partnerStatusesMap={partnerStatusesMap}
          searchValue={searchValue}
          checkableRows={bulkDelete}
          onCheckAllRowsToggle={onCheckAllRowsToggle}
          checkedTicketIds={checkedTicketIds}
          onRowCheck={onRowCheck}
          withExpandableRows={false}
          providerTicketBoard={providerTicketBoard}
        />

        <RowsCountAndPagination
          countText={t('ticketProviderTicketsPage.ticketsCount', { count: ticketsCount })}
          activePage={activePage}
          totalPages={totalPages}
          isFetching={isFetching}
          dataTest='tickets-count'
        />
      </Content>
    </MainWrap>
  )
}

export default TicketProviderTicketsPage
